import _ from 'underscore';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import {
  recommendationConfigs,
  recommendationDynamicTextConfigs
} from '../recommendation-configs';
import {
  RECOMMENDATION_TYPES,
  BUDGET_OPPORTUNITY_TYPES,
} from '../consts';
import {
  formatDecimal,
  formatEntityType,
  formatEntityNameForHtml,
} from './basic-format-data';
import {
  formatEstimates,
  formatPictchingSource,
  formatLocaledSeasonalEventType,
  formatCoupon,
} from './format-data';
import {
  isCompetition,
  isParentLevel,
  getNewTextConfigByPermission,
} from '../util';
import {
  formatPercentHundredths
} from './index';

/**
 * @param {Object} options - options for formatting summary model
 * @param {string} options.type - recommendation type
 * @param {string} options.level - target level of the recommendation.
 * For example, Device bid boost and Location bid boost can target Campaign or AdGroup.
 * @param {string} options.scope - current scope user selected in wunderbar
 * @param {Object} options.sample - sample data for summary card (insight, actions)
 * @param {number} options.recommendationsCount - total count of recommendations
 * @param {boolean} [options.isHighConverting=false] - if the ALL recommendations for
 * this type is HighConverting.
 * @param {boolean} [options.isSeasonal=false] - if recommendation is seasonal
 * @param {Object} options.i18n - Localization component
 * @return {Object} formatted summary model for React component to render
 */
export const formatSummary = (options) => {
  const {
    type,
    level,
    scope,
    recommendationsCount,
    sample,
    i18n,
    newI18n,
    isHighConverting = false,
    isSeasonal = false,
    permissions,
    isNrt,
    hasAIAssetRecommendation,
    isFeedbackPersonalized,
    currentActivity,
    showAutoApplyBanner,
    isAutoApplyRecommendationEnabled,
    thirdPartyInfos,
  } = options;

  // entity
  const entityType = formatEntityType(level, recommendationsCount, i18n);
  const entityName = sample && sample.target && formatEntityNameForHtml(level, sample.target);

  let recommendationConfig = recommendationConfigs[type];
  if (_.isUndefined(recommendationConfig)) {
    if (!_.isUndefined(currentActivity)) {
      currentActivity.error(`formatSummary: cannot get recommendationConfig, type=${type}`, type);
    }
    return null;
  }

  const useNewI18n = !_.isUndefined(recommendationConfig.useNewI18n) ?
    recommendationConfig.useNewI18n : false;
  const i18nToUse = useNewI18n ? newI18n : i18n;

  if (recommendationConfig.isDynamicText) {
    recommendationConfig = _.extend(
      recommendationConfig,
      recommendationDynamicTextConfigs[type](options)
    );
  }
  // title
  const title = i18nToUse.getString(
    recommendationConfig.summaryTitle,
    { entity_type: formatEntityType(level, 1, i18n) }
  );

  // description
  let descriptionKey = recommendationConfig.detailDescription;
  // pitching
  let pitchingKey = type === RECOMMENDATION_TYPES.COMPETITIVE_BID && isHighConverting ?
    `${recommendationConfig.detailPitching}_High_Converting` :
    recommendationConfig.detailPitching;
  const isScopeToThis = !useNewI18n && (isParentLevel(scope, level) || scope === level);

  const newConfig = getNewTextConfigByPermission(type, permissions, 'configs');

  if (newConfig) {
    descriptionKey = newConfig.detailDescription || descriptionKey;
    pitchingKey = newConfig.detailPitching || pitchingKey;
  }

  let description = i18nToUse.getString(
    isScopeToThis ? `${descriptionKey}_This` : descriptionKey,
    {
      n: recommendationsCount,
      entity_name: entityName,
      entity_type: entityType,
    }
  );

  if (isSeasonal && _.contains(BUDGET_OPPORTUNITY_TYPES, type)) {
    description = i18n.getString(
      `${descriptionKey}_Seasonal_Event`,
      { seasonalEvent: formatLocaledSeasonalEventType(i18n, sample.seasonalEvent) }
    );
  }


  let pitching = '';
  if (isCompetition(type)) {
    pitching = i18n.getString(
      pitchingKey,
      { segment: i18n.getString(`Details_View_Device_Type_${sample.segment}`) || sample.segment }
    );
  } else {
    switch (type) {
      case RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY:
      case RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY:
        pitching = isSeasonal ? i18n.getString(`${pitchingKey}_Seasonal_Event`) : i18n.getString(`${pitchingKey}`);
        break;
      case RECOMMENDATION_TYPES.GOOGLE_IMPORT:
      case RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED:
        pitching = i18n.getString(
          pitchingKey,
          { clicks: formatDecimal(options.estimates.clicksIncrease, i18n) }
        );
        break;
      case RECOMMENDATION_TYPES.SITE_LINK:
      case RECOMMENDATION_TYPES.CALLOUT: {
        const withThis = scope === levelAtConstant.CAMPAIGN || scope === levelAtConstant.ADGROUP ? '_This' : '';
        const cpcCondition = options.estimates.cpcIncrease > 0 ? '_cpcIncrease' : '_cpcDecrease';
        const cpcStatus = options.estimates.cpcIncrease === 0 ? '_cpcSame' : cpcCondition;
        const paramWithoutEstimates = { n: recommendationsCount, entity_type: entityType };
        const paramWithEstimates = {
          ...paramWithoutEstimates,
          ctrLift: formatPercentHundredths(options.estimates.ctrLift, i18n),
          cpcIncrease: formatDecimal(Math.abs(options.estimates.cpcIncrease), i18n),
        };
        if (options.estimates.ctrLift !== 0) {
          pitching = i18n.getString(`${pitchingKey}${cpcStatus}${withThis}`, paramWithEstimates);
        } else {
          pitching = i18n.getString(`${pitchingKey}_Without_Estimates${withThis}`, paramWithoutEstimates);
        }
        break;
      }
      case RECOMMENDATION_TYPES.NEW_KEYWORD_OPPORTUNITY:
        pitching = formatPictchingSource(
          i18n,
          pitchingKey,
          options.seasonalEntity.sources,
          options.seasonalEntity.seasonalEvent
        );
        break;
      case RECOMMENDATION_TYPES.REPAIR_ADS:
        pitching = isNrt ? i18n.getString('Summary_Card_Detail_Pitching_Repair_Ads_NRT') : i18n.getString(pitchingKey);
        break;
      case RECOMMENDATION_TYPES.REPAIR_KEYWORD:
        pitching = isNrt ? i18n.getString('Summary_Card_Detail_Pitching_Repair_Keywords_NRT') : i18n.getString(pitchingKey);
        break;
      case RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY:
        pitching = i18n.getString(pitchingKey, {
          n: recommendationsCount,
        });
        break;
      case RECOMMENDATION_TYPES.REMOVE_CONFLICTING_NEGATIVE_KEYWORD:
      case RECOMMENDATION_TYPES.BROAD_MATCH_KEYWORD:
      case RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS:
      case RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS:
      case RECOMMENDATION_TYPES.FIX_CONVERSION_GOAL_SETTINGS:
      case RECOMMENDATION_TYPES.TRENDING_QUERY:
      case RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL:
      case RECOMMENDATION_TYPES.FACEBOOK_IMPORT:
      case RECOMMENDATION_TYPES.MULTIMEDIA_ADS:
      case RECOMMENDATION_TYPES.IMAGE_EXTENSION:
      case RECOMMENDATION_TYPES.REPAIR_MISSING_KEYWORD_PARAMS:
      case RECOMMENDATION_TYPES.FIX_NO_IMPRESSION_BID:
      case RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS:
      case RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION:
      case RECOMMENDATION_TYPES.PMAX_IMPORT:
      case RECOMMENDATION_TYPES.DSA_2_PMAX_MIGRATION:
      case RECOMMENDATION_TYPES.SYNDICATION_GAP:
      case RECOMMENDATION_TYPES.AUDIENCE_VIDEOASSET:
      default:
        pitching = i18nToUse.getString(pitchingKey);
        break;
    }
  }

  let iconClasses;
  let iconClassesVNext;
  if (isSeasonal) {
    iconClasses = 'recommendation-seasonal-icon';
    iconClassesVNext = 'FavoriteStar';
  } else if (showAutoApplyBanner && isAutoApplyRecommendationEnabled) {
    iconClasses = 'recommendation-auto-apply-icon';
    iconClassesVNext = 'FlashAuto';
  } else {
    iconClasses = recommendationConfig.summaryIconClasses;
    iconClassesVNext = recommendationConfig.summaryIconClassesVNext;
  }
  const helpId = recommendationConfig.detailHelpId;
  const linkType = recommendationConfig.detailLinkType;
  const isEstimate = _.isUndefined(recommendationConfig.isEstimate) ?
    true : recommendationConfig.isEstimate;
  const { needNRTDisclaimer } = recommendationConfig;

  const estimates = formatEstimates(options);
  const coupon = options.coupon && formatCoupon(options);

  return _.extend(
    {
      iconClasses,
      iconClassesVNext,
      title,
      description,
      pitching,
      helpId,
      isSeasonal,
      isEstimate,
      needNRTDisclaimer,
      isNrt,
      hasAIAssetRecommendation,
      linkType,
      isFeedbackPersonalized,
      thirdPartyInfos,
    },
    estimates ? { estimates } : null,
    coupon ? { coupon } : null
  );
};
